import React, { useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { hasUserPermission } from '../../../utils/permissionHelper'
import { fetchWithTimeoutAndRetry } from '../../../utils/fetchHelper'
import { faQuestionCircle, faRefresh, faInfo, faSpinner, faAward, faCaretRight, faCaretDown } from '@fortawesome/free-solid-svg-icons'
import { format, parseISO } from 'date-fns'

// Data Modules, CONSTANTS, Helpers
import { useRequest } from '../../../hooks/UseRequest'

//COMPONENTS
import TableRow from '../../../components/TableRow'
import TableHeader from '../../../components/TableHeader'

//REDUX
import { setNotification } from '../../../redux/notificationSlice'
import { selectUser } from '../../../redux/userSlice'
import { selectAuth } from '../../../redux/authSlice'
import { setModal } from '../../../redux/modalSlice'
import Loading from "../../../components/Loading"
import { selectDropdownOptions } from '../../../redux/dropdownSlice'
import { selectPaymentSchedulePanel, setChangeMade, setSliderName } from '../../../redux/paymentSchedulePanelSlice'
import { setPaymentScheduleData, setCancelLoanFunction, setRefreshPaymentSchedule, selectPaymentScheduleData } from '../../../redux/paymentScheduleDataSlice'
import { setSimulatedDate, setNeedsRefresh } from '../../../redux/simulatedDateSlice'

import PaymentScheduleSlider from './PaymentScheduleSlider'
import { CurrencyFormat, PercentFormat, FormatISODate, DateFormat } from '../../../helpers/formatting'
import { selectSubpanelRerender, triggerRerender } from "../../../redux/rerenderSlice";
import LoanServiceButtons from '../../../components/LoanServiceButtons'
import ManagerApprovalIcons from '../../../components/elements/ManagerApprovalIcons'
import PromoTag from "../../../components/elements/PromoTag";
import { handleToolTipMouseEnter } from '../../../helpers/layout'

export default function PaymentScheduleSubPanel(props) {
  const { module, subModule, paymentPlanPaymentModule, data, id, setLoanInfo, loanInfo, customerLocked, submitUnlock, currentDate, setCurrentDate } = props
  const params = useParams()
  const request = useRequest()
  const dispatch = useDispatch()
  const navi = useNavigate()
  const requesting = useRef(false)
  const {
    register, handleSubmit, control, setError, formState: { errors }
  } = useForm();

  const [paymentId, setPaymentId] = useState(0)

  const toDisplayColumns = {
    id: 'ID',
    scheduled_date: 'Date',
    payment_status_dropdown_option_id: 'Status',
    payment_method_dropdown_option_id: 'Method',
    payment_type_dropdown_option_id: 'Type',
    scheduled_amount: 'Total',
    principal_amount: 'Principal',
    interest_amount: 'Interest',
    late_fee: 'Late Fee',
    nsf_fee: 'NSF Fee',
    credit_reimbursement_amount: 'Credit',
  };

  const toDisplayColumnsForPaymentPlan = {
    id: 'ID',
    payment_date: 'Date',
    payment_method_dropdown_option_id: 'Method',
    total_amount: 'Total Amount',
    principal_amount: 'Principal',
    interest_amount: 'Interest',
    late_fee: 'Late Fee',
    nsf_fee: 'NSF Fee',
  };

  //REDUX Selectors
  const auth = useSelector(selectAuth)
  const active = useSelector(selectUser)
  const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
  const dropdownOptions = useSelector(selectDropdownOptions)
  const paymentSchedulePanel = useSelector(selectPaymentSchedulePanel)
  const paymentScheduleData = useSelector(selectPaymentScheduleData)

  //State
  const [filteredModuleFields, setFilteredModuleFields] = useState(null)
  const [row, setRow] = useState(null)
  const [startId, setStartId] = useState(0)
  const [count, setCount] = useState(0)
  const [responseData, setResponseData] = useState([])
  const [loanId, setLoanId] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const rerenderValue = useSelector((state) => selectSubpanelRerender(state, 'payment_schedule'));
  const [isInCollection, setIsInCollection] = useState(false)
  // Resolution loans that have already had their binding amount recorded
  // (Step 1 QA flow) but haven't yet had a payment schedule attached. The
  // wizard's Step 1 plan-type buttons key off this flag — when true, only
  // the "Create Resolution Plan" button renders.
  const [isInResolutionWithoutPlan, setIsInResolutionWithoutPlan] = useState(false)
  // Resolution loans that already have an active fleshed plan (substatus =
  // `loan_sub_status_resolution_plan`). The Payment Plan toolbar button
  // surfaces for this state too, so an agent can open the wizard, hit the
  // step0 cancel-existing dialogue, and roll into a new plan + schedule
  // — same affordance settlement plans get.
  const [isInResolutionPlan, setIsInResolutionPlan] = useState(false)
  const [nextRegularPaymentId, setNextRegularPaymentId] = useState(0)
  const [existingPaymentPlan, setExistingPaymentPlan] = useState([])
  const [forgivenTotal, setForgivenTotal] = useState(0)
  const [forgivenPrincipal, setForgivenPrincipal] = useState(0)
  const [forgivenInterest, setForgivenInterest] = useState(0)
  const [forgivenLateFee, setForgivenLateFee] = useState(0)
  const [forgivenNsfFee, setForgivenNsfFee] = useState(0)
  const [filteredPaymentPlanPaymentModuleFields, setFilteredPaymentPlanPaymentModuleFields] = useState(null)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const promoCodeRedeemedStatusID = dropdownOptions.find((x) => x?.code_key === 'promo_code_status_redeemed')?.id
  const promoCodeRevokedStatusID = dropdownOptions.find((x) => x?.code_key === 'promo_code_status_revoked')?.id
  const idSpan = hasUserPermission(activePermissions, 'show_ids') ? 6 : 5
  const hasPaymentScheduledDetailedPermission = hasUserPermission(activePermissions, 'payment_scheduled_detailed_view')
  const [isDetailView, setIsDetailView] = useState(false)

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (module && auth.accessToken) {
      const filteredFields = [];
      const filteredPaymentPlanFields = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(toDisplayColumns)) {
        let column = subModule.module_fields.find((j) => j.column_name === key);
        if (column) {
          filteredFields.push({ ...column, label_name: value })
        }
      }
      setFilteredModuleFields(filteredFields)
      setLoanId(loanInfo?.id)

      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(toDisplayColumnsForPaymentPlan)) {
        let column = paymentPlanPaymentModule.module_fields.find((j) => j.column_name === key);
        if (column) {
          filteredPaymentPlanFields.push({ ...column, label_name: value })
        }
      }
      setFilteredPaymentPlanPaymentModuleFields(filteredPaymentPlanFields)
    }
  }, [module, auth.accessToken])

  useEffect(() => {
    if (loanId !== 0) {
      getPaymentSchedule(loanId)
    }
  }, [loanId])

  /**
   * Get record data from api based on endpoint
   * @returns {Promise<void>}
   */
  const getPaymentSchedule = async (loanId) => {
    if (requesting.current) return;
    setLoaded(true)
    requesting.current = true;
    try {
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule`;
      const queryParams = [];

      // Check if the currentDate variable has a value and, if so, add it as a query parameter
      if (currentDate !== null) {
        queryParams.push(`current_date=${currentDate}`);
      }

      if (isDetailView) {
        queryParams.push(`is_detail_view=1`);
      }

      if (queryParams.length > 0) {
        endpoint += `?${queryParams.join('&')}`;
      }
      request.setEndpoint(endpoint)
      // Mount-critical — see fetchHelper.js. Gates the toolbar buttons.
      // Timeout raised to 30s: the totals computation can legitimately run
      // long for loans with large payment histories / detail view.
      const response = await fetchWithTimeoutAndRetry(() => request.get(), { timeoutMs: 30_000 })
      setRow(response?.data?.data?.payments)
      setResponseData(response?.data?.data)
      let isLoanInCollection = response?.data?.data?.loan_status_dropdown_option_id == dropdownOptions.filter((o) => o.code_key === "loan_status_collections")[0].id
      let isLoanInResolution = response?.data?.data?.loan_status_dropdown_option_id == dropdownOptions.filter((o) => o.code_key === "loan_status_resolution")[0].id
      let noResPlanSubStatusId = dropdownOptions.filter((o) => o.code_key === "loan_sub_status_no_res_plan")[0]?.id
      let resolutionPlanSubStatusId = dropdownOptions.filter((o) => o.code_key === "loan_sub_status_resolution_plan")[0]?.id
      // Two resolution sub-states open the Payment Plan slider:
      //   - `no_res_plan`: QA Resolution recorded a binding amount but the
      //     schedule hasn't been fleshed yet — wizard goes straight to Step 1.
      //   - `resolution_plan`: there's an active fleshed plan — wizard opens
      //     on Step 0 (cancel-existing-to-replace), then drops back to Step 1
      //     after delete so the agent can create a new plan + schedule.
      let isLoanInResolutionWithoutPlan = isLoanInResolution
        && response?.data?.data?.loan_sub_status_dropdown_option_id == noResPlanSubStatusId
      let isLoanInResolutionPlan = isLoanInResolution
        && response?.data?.data?.loan_sub_status_dropdown_option_id == resolutionPlanSubStatusId
      setIsInCollection(isLoanInCollection)
      setIsInResolutionWithoutPlan(isLoanInResolutionWithoutPlan)
      setIsInResolutionPlan(isLoanInResolutionPlan)
      findNextRegularPayment(response?.data?.data?.payments)
      dispatch(setPaymentScheduleData(response?.data?.data));
      dispatch(setCancelLoanFunction(submitLoanCancellation));
      dispatch(setRefreshPaymentSchedule(refreshPaymentSchedule));
      setLoaded(false)
      requesting.current = false;
      // load payment plan using the loan id if the loan is in collections or resolution
      if (isLoanInCollection || isLoanInResolution) {
        getCurrentPaymentPlan(loanId)
      }
    } catch (error) {
      setRow([])
      setLoaded(false)
      // Surface the real error. API failures carry it on response.data.message;
      // a client-side throw (e.g. a missing dropdown option) only has error.message,
      // which is why this used to render a blank notification.
      const errorMessage = error?.response?.data?.message
        ?? error?.response?.data?.messageExtreme
        ?? error?.message
        ?? 'Unknown error loading payment schedule'
      dispatch(setNotification({ type: 'error', body: errorMessage }))
      requesting.current = false;
    }
  }

  const getCurrentPaymentPlan = async (loanId) => {
    if (requesting.current) return;
    requesting.current = true;
    try {
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_plan/existing`;
      // Check if the currentDate variable has a value and, if so, add it as a query parameter
      if (currentDate !== null) {
        endpoint += `?current_date=${currentDate}`;
      }
      request.setEndpoint(endpoint)
      const response = await request.get()
      setExistingPaymentPlan(response?.data?.data)
      if (response?.data?.data?.forgiven_principal) {
        setForgivenTotal(+response.data.data.forgiven_principal + +response.data.data.forgiven_interest + +response.data.data.forgiven_late_fee + +response.data.data.forgiven_nsf_fee);
        setForgivenPrincipal(+response.data.data.forgiven_principal);
        setForgivenInterest(+response.data.data.forgiven_interest);
        setForgivenLateFee(+response.data.data.forgiven_late_fee);
        setForgivenNsfFee(+response.data.data.forgiven_nsf_fee);
      }
      requesting.current = false;
    } catch (error) {
      setExistingPaymentPlan([]);
      requesting.current = false;
    }
  }

  /**
   * Finds the next regular payment based on certain conditions.
   * @param {Array} payments - An array of payment objects.
   * @returns {void} - This function sets the next regular payment ID through a separate function.
   */
  const findNextRegularPayment = (payments) => {
    const nextPayment = payments.find((payment) => {
      if (
        payment.payment_status_dropdown_option_id === dropdownOptions.filter((o) => o.code_key === "payment_status_scheduled")[0].id &&
        payment.payment_type_dropdown_option_id === dropdownOptions.filter((o) => o.code_key === "payment_type_regular")[0].id
      ) {
        return payment.id;
      }
    });
    setNextRegularPaymentId(nextPayment?.id ?? 0);
  };

  const refreshPaymentSchedule = async () => {
    if (loanId != 0) {
      getPaymentSchedule(loanId)
      dispatch(triggerRerender({ subpanelId: 'customer_email_message' }))
    }
  }

  const navigateSlider = (newSliderName = "") => {
    if (newSliderName === "form_submit") {
      // Reset all states when the payment panel form is successfully submitted
      dispatch(setChangeMade(false));
      dispatch(setSliderName(""));
    } else if (paymentSchedulePanel.sliderName === newSliderName) {
      // Handle the case when the same payment panel button is toggled
      checkChangeMade("");
    } else {
      // Switch to a new panel and check for changes made
      checkChangeMade(newSliderName);
    }
    submitUnlock()
  };

  const checkChangeMade = (newSliderName) => {
    if (paymentSchedulePanel.changeMade) {
      displayWarning(newSliderName);
    } else {
      dispatch(setSliderName(newSliderName));
    }
  };

  const displayWarning = (newSliderName = "") => {
    dispatch(setModal({
      is_active: true,
      type: 'are_you_sure',
      options: {
        title: paymentSchedulePanel.sliderName,
        description: "Are you sure you want to lose your work?",
        actionLabel: "Yes",
        callMethod: () => {
          dispatch(setSliderName(newSliderName));
          dispatch(setChangeMade(false));
        }
      }
    }));
  };

  const handleDateChange = (event) => {
    try {
      const formattedDate = format(new Date(event.target.value), 'yyyy-MM-dd HH:mm:ss');
      // Dispatch to global clock — parent syncs back to local currentDate via useEffect
      dispatch(setSimulatedDate({ simulated_date: formattedDate }));
      dispatch(setNeedsRefresh(true));
    } catch (error) {
      dispatch(setSimulatedDate({ simulated_date: null }));
    }
  };

  useEffect(() => {
    refreshPaymentSchedule();
  }, [currentDate, rerenderValue, isDetailView])

  const updatePaymentStatus = async (paymentId, paymentStatus, scheduleToNextBusinessDay = false) => {
    if (requesting.current) return;
    requesting.current = true;
    setLoaded(true)
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment/${paymentId}/status`)
      let params = { payment_status_dropdown_option_id: dropdownOptions.filter((o) => o.code_key === paymentStatus)[0].id }
      if (currentDate !== null) {
        params.current_date = currentDate;
      }
      if (scheduleToNextBusinessDay) {
        params.schedule_to_next_business_day = true;
      }
      await request.modify(params)
      requesting.current = false;
      getPaymentSchedule(loanId)
      dispatch(setNotification({ type: 'success', body: '' }))

      // refresh action panel
      dispatch(triggerRerender({ subpanelId: 'customer_action_button' }));
    } catch (error) {
      setLoaded(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
      requesting.current = false;
    }
  }

  const revertReschedule = async (paymentId) => {
    if (requesting.current) return;
    requesting.current = true;
    setLoaded(true)
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment/${paymentId}/revert_reschedule`)
      let params = {}
      if (currentDate !== null) {
        params.current_date = currentDate;
      }
      await request.modify(params)
      requesting.current = false;
      getPaymentSchedule(loanId)
      dispatch(setNotification({ type: 'success', body: '' }))
    } catch (error) {
      setLoaded(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
      requesting.current = false;
    }
  }

  const undiscountPayment = async (paymentId) => {
    if (requesting.current) return;
    requesting.current = true;
    setLoaded(true)
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment/${paymentId}/undiscount`)
      let params = {}
      if (currentDate !== null) {
        params.current_date = currentDate;
      }
      const response = await request.modify(params)
      requesting.current = false;
      getPaymentSchedule(loanId)
      dispatch(setNotification({ type: 'success', body: '' }))
      setLoaded(false)
    } catch (error) {
      setLoaded(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
      requesting.current = false;
    }
  }

  const undeferPayment = async (paymentId) => {
    if (requesting.current) return;
    requesting.current = true;
    setLoaded(true)
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment/${paymentId}/undefer`)
      let params = {}
      if (currentDate !== null) {
        params.current_date = currentDate;
      }
      const response = await request.modify(params)
      requesting.current = false;
      getPaymentSchedule(loanId)
      dispatch(setNotification({ type: 'success', body: '' }))
      setLoaded(false)
    } catch (error) {
      setLoaded(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
      requesting.current = false;
    }
  }

  const revertDeferr = async (paymentId) => {
    await dispatch(setModal({
      is_active: true,
      type: 'are_you_sure',
      options: {
        title: "Revert Defer",
        description: "Are you sure you want to revert this split deferred payment caused by a paydown?",
        actionLabel: "Yes",
        callMethod: async () => {
          if (requesting.current) return;
          requesting.current = true;
          setLoaded(true)
          try {
            request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment/${paymentId}/revert_defer`)
            let params = {}
            if (currentDate !== null) {
              params.current_date = currentDate;
            }
            await request.modify(params)
            requesting.current = false;
            getPaymentSchedule(loanId)
            dispatch(setNotification({ type: 'success', body: '' }))
          } catch (error) {
            setLoaded(false)
            const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
            dispatch(setNotification({ type: 'error', body: errorMessage }))
            requesting.current = false;
          }
        }
      }
    }))
  }

  const recordChargeback = async (paymentId) => {
    if (requesting.current) return;
    requesting.current = true;
    setLoaded(true)
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment/${paymentId}/chargeback/record`)
      let params = {}
      if (currentDate !== null) {
        params.current_date = currentDate;
      }
      const response = await request.modify(params)
      requesting.current = false;
      getPaymentSchedule(loanId)
      dispatch(setNotification({ type: 'success', body: '' }))
      setLoaded(false)

      // refresh action panel
      dispatch(triggerRerender({ subpanelId: 'customer_action_button' }));
    } catch (error) {
      setLoaded(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
      requesting.current = false;
    }
  }

  const deletePayment = async (paymentId) => {
    await dispatch(setModal({ is_active: true, type: 'delete_record', options: { baseName: `loan/${loanId}/payment`, id: paymentId, currentDate: currentDate } }))
  }

  const discountPayment = async (paymentId) => {
    setPaymentId(paymentId)
    navigateSlider('discount')
  }

  const submitLoanCancellation = async () => {
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule/cancellation`)
    try {
      let body = {}
      if (currentDate !== null) {
        body['current_date'] = currentDate;
      }
      const response = await request.post(body)
      if (response.status === 200 || response.status === 201) {
        dispatch(setNotification({ type: 'success', body: '' }))
        refreshPaymentSchedule()
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const promotionRedeem = async (customer_promotion_code_id) => {
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/promotion/redeem`)
    try {
      let body = {}
      if (currentDate !== null) {
        body.current_date = currentDate;
      }
      body.customer_promotion_code_id = customer_promotion_code_id
      const response = await request.post(body)
      if (response.status === 200 || response.status === 201) {
        dispatch(setNotification({ type: 'success', body: '' }))
        refreshPaymentSchedule()
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const promoCheck = () => {
    if (responseData && responseData.active_promotions && responseData.active_promotions.length > 0) {
      return responseData.active_promotions.map((promotion, index) => {
        if (promotion.is_winback) {
          return <>{index > 0 ? ' ' : ''} <PromoTag promoType="winback" /> </>
        } else if (promotion.name.startsWith("Fresh Start")) {
          return <>{index > 0 ? ' ' : ''} <PromoTag promoType="fresh_start" /> </>
        } else {
          switch (promotion.promo_code_status_dropdown_option_id) {
            case promoCodeRedeemedStatusID:
              return <>{index > 0 ? ' ' : ''} {promotion.name} (Redeemed) </>;
            case promoCodeRevokedStatusID:
              return <>{index > 0 ? ' ' : ''} {promotion.name} (Revoked) </>;
            default:
              return <>{index > 0 ? ' ' : ''} {promotion.name}</>;
          }
        }
      })
    }
    return null
  }

  const handleClick = (event) => {
    let sel = window.getSelection();
    sel.removeAllRanges();
    setIsCollapsed(!isCollapsed)
  }
  let rowData;
  if ((dropdownOptions.find((o) => o?.id === responseData?.loan_status_dropdown_option_id)?.code_key === "loan_status_collections")) {
    rowData = { afterDate: responseData?.collection_start_date }
  }

  return (
    <>
      {/* Loading Panel Header */}
      <div
        className="lox-sub-module-sub-panel column is-full is-flex is-justify-content-space-between is-align-items-center has-background-light p-1 mb-0"
      >
        <p className="has-text-dark has-text-weight-bold is-size-6 pl-5">
          Loan Status: <span
            data-testid="loan-info-status"
            className="has-text-weight-normal"
          >{responseData?.loan_status_dropdown_option_id ? ((dropdownOptions.find((o) => o?.id === responseData?.loan_status_dropdown_option_id)?.name) ?? '-') : '-'}</span>
          <span className="px-3 has-text-weight-normal">|</span>
          Loan Sub-Status: <span
            className="has-text-weight-normal"
          >{responseData?.loan_sub_status_dropdown_option_id ? ((dropdownOptions.find((o) => o?.id === responseData?.loan_sub_status_dropdown_option_id)?.name) ?? '-') : '-'}</span>
          <span className="px-3 has-text-weight-normal">|</span>
          Promotion(s): <span className="has-text-weight-normal">{promoCheck()}</span>
        </p>

        <p className="has-text-danger-dark has-text-weight-bold is-size-6 pl-5">
          {responseData?.pending_collections_milestone ? (
            <>
              {responseData.pending_collections_milestone.label} <span className="has-text-weight-normal"><DateFormat dateString={responseData.pending_collections_milestone.date} /></span><br />
            </>
          ) : null}
          {(responseData?.loan_clearing_date) && (
            <>
              Will move to Paid Off on: <span className="has-text-weight-normal"> <DateFormat dateString={responseData.loan_clearing_date} /> </span><br />
            </>
          )}
        </p>

      </div>
      {!row && (
        <div
          className="lox-sub-module-sub-panel column is-full is-flex is-justify-content-space-between is-align-items-center has-background-light p-1 mb-0"
        >
          <p className="has-text-dark has-text-weight-bold is-size-6 pl-5">
            Payment Schedule
            <Loading noWrapper />
          </p>
        </div>
      )}
      {/* Ready and have NO data Panel Header */}
      {row && row.length === 0 && (
        <div className="lox-sub-module-sub-panel column is-full is-flex is-justify-content-space-between is-align-items-center has-background-light p-1 mb-0">
          <p className="has-text-grey-lighter has-text-weight-bold is-size-6 pl-5">
            Payment Schedule
          </p>
        </div>
      )}
      {row && row.length > 0 && (
        <>
          <div className="lox-sub-module-sub-panel column is-full is-flex is-justify-content-space-between is-align-items-center has-background-light p-1 mb-0">
            <p className="has-text-dark has-text-weight-bold is-size-6 pl-5">Payment Schedule</p>
            <div className="is-flex is-align-items-end payment-schedule">
              {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
                <>
                  <p className="has-text-dark has-text-weight-bold is-size-5 px-2">
                    <input
                      name="current_date"
                      className="input is-small"
                      type="datetime-local"
                      value={currentDate ? currentDate.replace(' ', 'T') : ''}
                      onChange={handleDateChange}
                    />
                  </p>
                  <p className="has-text-dark has-text-weight-bold is-size-5 pr-2 is-clickable">
                    <button
                      type="button"
                      className="button has-background-grey has-text-white is-small px-3"
                      onClick={() => refreshPaymentSchedule()}
                    >
                      <FontAwesomeIcon icon={faRefresh} />
                    </button>
                  </p>
                </>
              )}

              {responseData && (
                <button
                  type="button"
                  data-testid="payment-schedule-action-reschedule"
                  className="button has-background-grey has-text-white is-small mr-1"
                  onClick={() => navigateSlider('reschedule')}
                  disabled={!responseData.can_reschedule_any_payment || customerLocked}
                >
                  <span className="mx-3">
                    Reschedule
                    <span
                      className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-top has-tooltip-primary'}
                      data-tooltip={'Moves the next upcoming Regular payment to a new date, NOT to go past the next Regularly scheduled payment. Note we do not change interest accrual as a result of this move.'}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  </span>
                </button>
              )}

              {responseData && !isInCollection && (
                <button
                  type="button"
                  data-testid="payment-schedule-action-defer"
                  className="button has-background-grey has-text-white is-small mr-1"
                  onClick={() => navigateSlider('defer')}
                  disabled={!responseData.can_defer_any_payment || customerLocked}
                >
                  <span className="mx-3">
                    Defer
                    <span
                      className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-top has-tooltip-primary'}
                      data-tooltip={'Defers the next Regular payment out in the schedule, typically 1 period past the existing end of the schedule. Note we do not change interest accrual as a result of this move.'}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  </span>
                </button>
              )}

              {responseData && !isInCollection && (
                <button
                  type="button"
                  data-testid="payment-schedule-action-makeup"
                  className="button has-background-grey has-text-white is-small mr-1"
                  onClick={() => navigateSlider('makeup')}
                  disabled={!responseData.can_create_any_makeup || customerLocked}
                >
                  <span className="mx-3">
                    Makeup
                    <span
                      className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-top has-tooltip-primary'}
                      data-tooltip={'Creates one or more payments to makeup the outstanding balance due to a missed/failed/returned payment.'}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  </span>
                </button>
              )}

              {responseData && (
                <button
                  type="button"
                  data-testid="payment-schedule-action-paydown"
                  className="button has-background-grey has-text-white is-small mr-1"
                  onClick={() => navigateSlider('paydown')}
                  disabled={!responseData.can_create_any_paydown || customerLocked}
                >
                  <span className="mx-3">
                    Paydown
                    <span
                      className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-top has-tooltip-primary'}
                      data-tooltip={'Creates a payment that applies solely to the principal (unless there are outstanding fees, then it applies there first). If deferred payment in future, un-defer payment and apply $$ to that payment first'}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  </span>
                </button>
              )}

              {responseData && !isInCollection && (
                <button
                  type="button"
                  data-testid="payment-schedule-action-payoff"
                  className="button has-background-grey has-text-white is-small mr-1"
                  onClick={() => navigateSlider('payoff')}
                  disabled={!responseData.can_create_any_payoff || customerLocked}
                >
                  <span className="mx-3">
                    Payoff
                    <span
                      className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-top has-tooltip-primary'}
                      data-tooltip={'Creates a payment to pay off the loan in a single lump sum covering all accrued interest up to payoff date.'}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  </span>
                </button>
              )}

              <button
                type="button"
                data-testid="payment-schedule-action-credit"
                className="button has-background-grey has-text-white is-small mr-1"
                onClick={() => navigateSlider('credit')}
                disabled={!responseData.can_create_any_credit || customerLocked}
              >
                <span className="mx-3">
                  Credit
                  <span
                    className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-left has-tooltip-primary'}
                    data-tooltip={'Allows agent to credit customer back due to mistakes on our end.'}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                  </span>
                </span>
              </button>

              {responseData && (isInCollection || isInResolutionWithoutPlan || isInResolutionPlan) && (
                <button
                  type="button"
                  data-testid="payment-schedule-action-payment-plan"
                  className="button has-background-grey has-text-white is-small mr-1"
                  onClick={() => navigateSlider('payment_plan')}
                  disabled={customerLocked || !responseData.can_access_payment_plan}
                >
                  <span className="mx-3">
                    Payment Plan
                    <span
                      className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-left has-tooltip-primary'}
                      data-tooltip={'This allows agent to setup a collections or settlement plan with customer in collections.'}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  </span>
                </button>
              )}
              {hasPaymentScheduledDetailedPermission && (
                <button
                  type="button"
                  data-testid="payment-schedule-action-detail-view"
                  className={`button has-text-white is-small mr-1 ${isDetailView ? 'has-background-primary' : 'has-background-grey'}`}
                  onClick={() => setIsDetailView((prev) => !prev)}
                >
                  <span className="mx-3">
                    Detail View
                    <span
                      className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-left has-tooltip-primary'}
                      data-tooltip={'Activates a detailed view of the payment schedule, including zeroed payments, all holds, actual vs. scheduled notations, all adjustments, and deleted payments.'}
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  </span>
                </button>
              )}

              {responseData && (
                <LoanServiceButtons
                  loanId={loanId}
                  currentDate={currentDate}
                  submitUnlock={submitUnlock}
                  customerLocked={customerLocked}
                  active={active}
                  refreshPaymentSchedule={refreshPaymentSchedule}
                  availablePaymentMethods={responseData.payment_method_dropdown_options}
                  loanData={responseData}
                />
              )}
            </div>
          </div>
          <PaymentScheduleSlider
            loanId={loanId}
            refreshPaymentSchedule={refreshPaymentSchedule}
            currentDate={currentDate}
            navigateSlider={navigateSlider}
            paymentId={paymentId}
            outstandingTotal={responseData?.outstanding_total}
            promotions={responseData?.active_promotions}
            customerInfo={data}
            loanInfo={loanInfo}
            isInResolutionWithoutPlan={isInResolutionWithoutPlan}
          />
          <div className="lox-hooktable lox-sub-panel table-container column is-full py-0 mb-1 pr-0 position-relative" style={{ overflow: 'visible' }}>
            <div className="subpanel-table-container">
              {loaded && (
                <div className="loader-wrapper">
                  <Loading />
                </div>
              )}
              <table className="lox-sub-panel--table table is-striped is-narrow is-hoverable is-fullwidth is-info has-text-centered has-sticky-column has-action-buttons">
                <TableHeader columns={filteredModuleFields} hasActions startId={startId} isLightColor />
                <tbody>
                  <TableRow
                    rows={row}
                    columns={filteredModuleFields}
                    hasActions
                    baseName="payment_schedule"
                    revertReschedule={revertReschedule}
                    revertDeferr={revertDeferr}
                    updatePaymentStatus={updatePaymentStatus}
                    deletePayment={deletePayment}
                    discountPayment={discountPayment}
                    navigateSlider={navigateSlider}
                    undiscountPayment={undiscountPayment}
                    undeferPayment={undeferPayment}
                    nextRegularPaymentId={nextRegularPaymentId}
                    promotionRedeem={promotionRedeem}
                    loanId={loanId}
                    refreshPaymentSchedule={refreshPaymentSchedule}
                    customerLocked={customerLocked}
                    recordChargeback={recordChargeback}
                    submitUnlock={submitUnlock}
                    customRowData={rowData}
                    customRowName="collections_date"
                    isSubpanel
                    isDetailView={isDetailView}
                  />
                  <tr className="has-background-lightgrey has-text-weight-bold has-text-white has-text-left is-nowrap">
                    <td className="has-text-center" colSpan={idSpan}>
                      Original Plan
                      <span
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                        data-tooltip={`Original Contract Owed Amounts ("Original" Interest will vary as interest accrues or is recalculated)`}
                        onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="pl-1 has-text-white" style={{ paddingBottom: '.15rem !important' }} />
                      </span>
                    </td>
                    <td><CurrencyFormat value={responseData?.totals.originalPlanTotal} /></td>
                    <td><CurrencyFormat value={responseData?.totals.contractedPrincipal} /></td>
                    <td><CurrencyFormat value={responseData?.totals.regular.interest} /></td>
                    <td>-</td>
                    <td>-</td>
                    <td className="has-background-lightgrey" colSpan={1} style={{ position: 'unset' }}></td>
                    <td className="has-background-lightgrey" colSpan={2} style={{ position: 'unset' }}></td>
                  </tr>
                  <tr className="has-background-lightgrey has-text-weight-bold has-text-white has-text-left is-nowrap">
                    <td className="has-text-center" colSpan={idSpan}>
                      Assessed Fees
                      <span
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                        data-tooltip="Amount of fees assessed from activites throughout the schedule."
                        onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="pl-1 has-text-white" style={{ paddingBottom: '.15rem !important' }} />
                      </span>
                    </td>
                    <td><CurrencyFormat value={responseData?.totals.assessedFeeTotal} /></td>
                    <td>-</td>
                    <td>-</td>
                    <td><CurrencyFormat value={responseData?.totals.assessedLateFee} /></td>
                    <td><CurrencyFormat value={responseData?.totals.assessedNsfFee} /></td>
                    <td className="has-background-lightgrey" colSpan={1} style={{ position: 'unset' }}></td>
                    <td className="has-background-lightgrey" colSpan={2} style={{ position: 'unset' }}></td>
                  </tr>
                  <tr className="has-background-blue has-text-weight-bold has-text-white has-text-left is-nowrap">
                    <td className="has-text-center" colSpan={idSpan}>
                      Original Plan + Fees
                      <span
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                        data-tooltip="Full starting point for the customers owed amount."
                        onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="pl-1 has-text-white" style={{ paddingBottom: '.15rem !important' }} />
                      </span>
                    </td>
                    <td><CurrencyFormat value={responseData?.totals.finalStartTotal} /></td>
                    <td><CurrencyFormat value={responseData?.totals.contractedPrincipal} /></td>
                    <td><CurrencyFormat value={responseData?.totals.regular.interest} /></td>
                    <td><CurrencyFormat value={responseData?.totals.assessedLateFee} /></td>
                    <td><CurrencyFormat value={responseData?.totals.assessedNsfFee} /></td>
                    <td className="has-background-blue" colSpan={1} style={{ position: 'unset' }}></td>
                    <td className="has-background-blue" colSpan={2} style={{ position: 'unset' }}></td>
                  </tr>
                  <tr className="has-background-success has-text-weight-bold has-text-white has-text-left is-nowrap">
                    <td className="has-text-center" colSpan={idSpan}>
                      Paid
                      <span
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                        data-tooltip="Amount actually paid by the customer. Sum of the actual_* fields on the payments."
                        onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="pl-1 has-text-white" style={{ paddingBottom: '.15rem !important' }} />
                      </span>
                    </td>
                    <td><CurrencyFormat value={responseData?.totals.paid.total} /></td>
                    <td><CurrencyFormat value={responseData?.totals.paid.principal} /></td>
                    <td><CurrencyFormat value={responseData?.totals.paid.interest} /></td>
                    <td><CurrencyFormat value={responseData?.totals.paid.late_fee} /></td>
                    <td><CurrencyFormat value={responseData?.totals.paid.nsf_fee} /></td>
                    <td className="has-background-success" colSpan={1} style={{ position: 'unset' }}></td>
                    <td className="has-background-success" colSpan={2} style={{ position: 'unset' }}></td>
                  </tr>
                  <tr className="has-background-lightgrey has-text-weight-bold has-text-white has-text-left is-nowrap">
                    <td className="has-text-center" colSpan={idSpan}>
                      Discounted
                      <span
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                        data-tooltip="Amount discouted by us for the customer. Sum of prediscount_* (or scheduled_*) - actual_* on the payments."
                        onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="pl-1 has-text-white" style={{ paddingBottom: '.15rem !important' }} />
                      </span>
                    </td>
                    <td><CurrencyFormat value={responseData?.totals.discounted.total} /></td>
                    <td><CurrencyFormat value={responseData?.totals.discounted.principal} /></td>
                    <td><CurrencyFormat value={responseData?.totals.discounted.interest} /></td>
                    <td><CurrencyFormat value={responseData?.totals.discounted.late_fee} /></td>
                    <td><CurrencyFormat value={responseData?.totals.discounted.nsf_fee} /></td>
                    <td className="has-background-lightgrey" colSpan={1} style={{ position: 'unset' }}></td>
                    <td className="has-background-lightgrey" colSpan={2} style={{ position: 'unset' }}></td>
                  </tr>
                  <tr className="has-background-grey has-text-weight-bold has-text-white has-text-left is-nowrap">
                    <td className="has-text-center" colSpan={idSpan}>
                      Sub-total
                      <span
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                        data-tooltip="Sum of paid + discounted amounts."
                        onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="pl-1 has-text-white" style={{ paddingBottom: '.15rem !important' }} />
                      </span>
                    </td>
                    <td><CurrencyFormat value={responseData?.totals.paidAndDiscountedSubtotal.total} /></td>
                    <td><CurrencyFormat value={responseData?.totals.paidAndDiscountedSubtotal.principal} /></td>
                    <td><CurrencyFormat value={responseData?.totals.paidAndDiscountedSubtotal.interest} /></td>
                    <td><CurrencyFormat value={responseData?.totals.paidAndDiscountedSubtotal.late_fee} /></td>
                    <td><CurrencyFormat value={responseData?.totals.paidAndDiscountedSubtotal.nsf_fee} /></td>
                    <td className="has-background-grey" colSpan={1} style={{ position: 'unset' }}></td>
                    <td className="has-background-grey" colSpan={2} style={{ position: 'unset' }}></td>
                  </tr>
                  <tr className="has-background-lightgrey has-text-weight-bold has-text-white has-text-left is-nowrap">
                    <td className="has-text-center" colSpan={idSpan}>
                      Outstanding
                      <span
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                        data-tooltip="Total remaining money owed by the customer. (Original Plan + Fees) - (Paid + Discounted)"
                        onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="pl-1 has-text-white" style={{ paddingBottom: '.15rem !important' }} />
                      </span>
                    </td>
                    <td><CurrencyFormat value={responseData?.totals.outstanding.total} /></td>
                    <td><CurrencyFormat value={responseData?.totals.outstanding.principal} /></td>
                    <td><CurrencyFormat value={responseData?.totals.outstanding.interest} /></td>
                    <td><CurrencyFormat value={responseData?.totals.outstanding.late_fee} /></td>
                    <td><CurrencyFormat value={responseData?.totals.outstanding.nsf_fee} /></td>
                    <td className="has-background-lightgrey" colSpan={1} style={{ position: 'unset' }}></td>
                    <td className="has-background-lightgrey" colSpan={2} style={{ position: 'unset' }}></td>
                  </tr>
                  <tr className="has-background-blue has-text-weight-bold has-text-white has-text-left is-nowrap">
                    <td className="has-text-center" colSpan={idSpan}>
                      Current Plan
                      <span
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                        data-tooltip="All amounts on the current schedule as if they were paid."
                        onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="pl-1 has-text-white" style={{ paddingBottom: '.15rem !important' }} />
                      </span>
                    </td>
                    <td><CurrencyFormat value={responseData?.totals.plan.total} /></td>
                    <td><CurrencyFormat value={responseData?.totals.plan.principal} /></td>
                    <td><CurrencyFormat value={responseData?.totals.plan.interest} /></td>
                    <td><CurrencyFormat value={responseData?.totals.plan.late_fee} /></td>
                    <td><CurrencyFormat value={responseData?.totals.plan.nsf_fee} /></td>
                    <td className="has-background-blue" colSpan={1} style={{ position: 'unset' }}></td>
                    <td className="has-background-blue" colSpan={2} style={{ position: 'unset' }}></td>
                  </tr>
                  <tr className="has-background-danger has-text-weight-bold has-text-white has-text-left is-nowrap">
                    <td className="has-text-center" colSpan={idSpan}>
                      Unscheduled
                      <span
                        className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                        data-tooltip="Amount owed by the customer not currently reflected on the schedule. (Original Plan + Fees) - (Plan + Discounted)"
                        onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="pl-1 has-text-white" style={{ paddingBottom: '.15rem !important' }} />
                      </span>
                    </td>
                    <td><CurrencyFormat value={responseData?.totals.unscheduled.total} /></td>
                    <td><CurrencyFormat value={responseData?.totals.unscheduled.principal} /></td>
                    <td><CurrencyFormat value={responseData?.totals.unscheduled.interest} /></td>
                    <td><CurrencyFormat value={responseData?.totals.unscheduled.late_fee} /></td>
                    <td><CurrencyFormat value={responseData?.totals.unscheduled.nsf_fee} /></td>
                    <td className="has-background-danger" colSpan={1} style={{ position: 'unset' }}></td>
                    <td className="has-background-danger" colSpan={2} style={{ position: 'unset' }}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {existingPaymentPlan && existingPaymentPlan?.payment_plan_payments && (
        <>
          <div onDoubleClick={handleClick} className="lox-sub-module-sub-panel column is-full is-flex is-justify-content-space-between is-align-items-center has-background-light p-1 mb-0">
            <p className="has-text-dark has-text-weight-bold is-size-6 pl-5">
              <span onClick={handleClick} style={{ cursor: 'pointer' }} className="mr-2">
                {isCollapsed ? <FontAwesomeIcon icon={faCaretRight} className="ml-0" /> : <FontAwesomeIcon icon={faCaretDown} className="ml-0" />}
              </span>
              Payment Plan
            </p>
          </div>
          <div className={`lox-hooktable lox-sub-panel table-container column is-full py-0 mb-1 pr-0 position-relative ${isCollapsed ? "is-hidden" : ""}`} style={{ overflow: 'visible' }}>
            <div className="subpanel-table-container" style={{ overflow: 'visible' }}>
              {loaded && (
                <div className="loader-wrapper">
                  <Loading />
                </div>
              )}
              <table className="lox-sub-panel--table table is-striped is-narrow is-hoverable is-fullwidth is-info has-text-centered has-sticky-column">
                <TableHeader columns={filteredPaymentPlanPaymentModuleFields} startId={startId} isLightColor />
                <tbody>
                  {existingPaymentPlan?.payment_plan_payments.length > 0 && (
                    <TableRow
                      rows={existingPaymentPlan?.payment_plan_payments}
                      columns={filteredPaymentPlanPaymentModuleFields}
                      baseName="payment_plan_payment"
                      hasActions={false}
                      isSubpanel
                    />
                  )}
                  <tr className="has-background-grey has-text-weight-bold has-text-white has-text-left is-nowrap">
                    <td className="has-background-grey" colSpan={4}>TOTAL PAYMENT PLAN</td>
                    <td className="has-background-grey"><CurrencyFormat value={existingPaymentPlan?.owed_amount} /></td>
                    <td className="has-background-grey"><CurrencyFormat value={existingPaymentPlan?.payment_plan_payments.length > 0 ? existingPaymentPlan.payment_plan_payments.reduce((total, item) => total + +item.principal_amount, 0) : null} /></td>
                    <td className="has-background-grey"><CurrencyFormat value={existingPaymentPlan?.payment_plan_payments.length > 0 ? existingPaymentPlan.payment_plan_payments.reduce((total, item) => total + +item.interest_amount, 0) : null} /></td>
                    <td className="has-background-grey"><CurrencyFormat value={existingPaymentPlan?.payment_plan_payments.length > 0 ? existingPaymentPlan.payment_plan_payments.reduce((total, item) => total + +item.late_fee, 0) : null} /></td>
                    <td className="has-background-grey"><CurrencyFormat value={existingPaymentPlan?.payment_plan_payments.length > 0 ? existingPaymentPlan.payment_plan_payments.reduce((total, item) => total + +item.nsf_fee, 0) : null} /></td>
                  </tr>
                  <tr className="has-background-grey has-text-weight-bold has-text-white has-text-left is-nowrap">
                    <td className="has-background-grey has-text-left " colSpan={9}>
                      <small>
                        Payment Plan: <a href={`/payment_plan/${existingPaymentPlan.id}`} className="note-detail-link-underline">{existingPaymentPlan?.label}</a>,{" "}
                        Number of Installments: {existingPaymentPlan?.number_of_installments},{" "}
                        Discount Percent: <PercentFormat value={existingPaymentPlan?.discount_percent} />,{" "}
                        Type: {dropdownOptions.find((x) => x?.id === existingPaymentPlan?.payment_plan_type_dropdown_option_id)?.name},{" "}
                        {existingPaymentPlan?.document_id ? "Document ID: " + existingPaymentPlan?.document_id : "No Document"},{" "}
                        Manager Approval: {existingPaymentPlan?.mgr_approval_status_dropdown_option_id ? (
                          <ManagerApprovalIcons type={`${dropdownOptions.find((x) => x?.id === existingPaymentPlan?.mgr_approval_status_dropdown_option_id)?.code_key}`} />
                        ) : "No Approval Needed"},{" "}
                        {existingPaymentPlan?.is_activated ? "Active Plan" : "Inactive Plan"},{" "}
                        Created by {existingPaymentPlan?.created_by_user?.label} on {FormatISODate(existingPaymentPlan?.created_at)}
                      </small>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  )
}
